import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {Feature, Map, View} from 'ol';
import {XYZ} from 'ol/source';
import TileLayer from "ol/layer/Tile";
import {fromLonLat, toLonLat} from "ol/proj";
import {Circle, Point} from "ol/geom";
import VectorSource from "ol/source/Vector";
import {Fill, Icon, Stroke, Style} from "ol/style";
import VectorLayer from "ol/layer/Vector";
import {HttpClient} from "@angular/common/http";
import CircleStyle from "ol/style/Circle";
import {isPlatformBrowser} from "@angular/common";
import {Config} from "../../../common/config";

@Component({
  selector: 'app-frontend-map',
  standalone: false,
  templateUrl: './frontend-map.component.html',
  styleUrls: ['./frontend-map.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FrontendMapComponent implements OnInit, OnChanges {
  @ViewChild('map')
  mapElement!: ElementRef;
  @Input()
  isClickable = true;
  @Input()
  geoReverseService = 'https://nominatim.openstreetmap.org/reverse.php?lat={latitude}&lon={longitude}&zoom=18&format=jsonv2';
  @Input()
  width?: string;
  @Input()
  height?: string;
  @Input()
  latitudePointer?: number;
  @Output()
  latitudePointerChange: EventEmitter<any> = new EventEmitter();
  @Input()
  longitudePointer?: number;
  @Output()
  longitudePointerChange: EventEmitter<any> = new EventEmitter();
  @Output()
  addressDetail: EventEmitter<any> = new EventEmitter();
  @Output()
  mapClicked: EventEmitter<{ latitude: number, longitude: number }> = new EventEmitter();
  @Input()
  showControlsCurrentLocation?: boolean;
  @Input()
  titleCurrentLocation = 'موقعیت فعلی';
  @Input()
  zoom = 14;
  @Input()
  showControlsZoom?: boolean = true;
  @Input()
  isRadiusSelectionEnable?: boolean = false;
  @Input()
  isAddressDetailEnable?: boolean = false;
  @Input()
  addressTitle?: string; // عنوان آدرس برای نمایش در دیالوگ انتخاب اپلیکیشن نقشه
  @Input()
  openMapOnClick: boolean = false; // آیا با کلیک روی نقشه، دیالوگ انتخاب اپلیکیشن باز شود
  map: Map | null = null;
  markerLayer: VectorLayer | undefined;
  radiusLayer: VectorLayer | undefined;
  @Input()
  radius: number = 1000;
  private originalLatitude?: number;
  private originalLongitude?: number;

  constructor(private httpClient: HttpClient) {
  }

  ngOnInit(): void {
    setTimeout(() => {
      if (!this.latitudePointer) {
        this.latitudePointer = 35.699737;
      }
      if (!this.longitudePointer) {
        this.longitudePointer = 51.338061;
      }
      // Store original coordinates for map click event
      this.originalLatitude = this.latitudePointer;
      this.originalLongitude = this.longitudePointer;
      if (isPlatformBrowser(Config.platformId)) {
        this.map = new Map({
          target: this.mapElement.nativeElement,
          layers: [
            new TileLayer({
              source: new XYZ({
                url: 'https://{a-c}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png'
              })
            })
          ]
        });
        this.map!.setView(new View({
          center: fromLonLat([this.longitudePointer, this.latitudePointer]),
          zoom: this.zoom
        }));
        if (this.isClickable) {
          this.map.on('click', (event) => {
            const clickedCoordinate: any = event.coordinate;
            const lonLat: any = toLonLat(clickedCoordinate);
            this.longitudePointer = lonLat[0];
            this.latitudePointer = lonLat[1];
            this.addMarker([this.longitudePointer!, this.latitudePointer!], true);
            setTimeout(() => {
              this.addMarker([this.longitudePointer!, this.latitudePointer!], false);
            }, 3000);
            if (this.isRadiusSelectionEnable) {
              this.addRadiusCircle(clickedCoordinate, this.radius);
            }
            // Emit map click event with original marker coordinates (branch location)
            if (this.originalLatitude && this.originalLongitude) {
              this.mapClicked.emit({
                latitude: this.originalLatitude,
                longitude: this.originalLongitude
              });
              // اگر openMapOnClick فعال باشد، دیالوگ انتخاب اپلیکیشن نقشه را باز می‌کنیم
              if (this.openMapOnClick) {
                this.openMapInDevice(this.originalLatitude, this.originalLongitude);
              }
            }
          });
        }
        this.addMarker([this.longitudePointer, this.latitudePointer]);
        if (this.isRadiusSelectionEnable) {
          this.addRadiusCircle([this.longitudePointer!, this.latitudePointer!], this.radius);
          this.updateRadius(this.radius);
        }
        if (this.showControlsCurrentLocation) {
          navigator.geolocation.getCurrentPosition((position: GeolocationPosition) => {
            this.latitudePointer = position.coords.latitude;
            this.longitudePointer = position.coords.longitude;
            this.addMarker([this.longitudePointer, this.latitudePointer], true);
            this.map?.getView().setCenter(fromLonLat([this.longitudePointer, this.latitudePointer]));
            setTimeout(() => {
              this.addMarker([this.longitudePointer!, this.latitudePointer!], false);
            }, 3000);
          });
        }
      }
    }, 200);
  }

  ngOnChanges(changes: any) {
    if (this.radiusLayer && changes.radius) {
      this.updateRadius(changes.radius.currentValue);
    }
    if (changes.latitudePointer || changes.longitudePointer) {
      if (this.map && this.latitudePointer && this.longitudePointer) {
        this.map.getView().setCenter(fromLonLat([this.longitudePointer, this.latitudePointer]));
        this.addMarker([this.longitudePointer, this.latitudePointer]);
        // Update original coordinates when they change from outside
        if (changes.latitudePointer?.firstChange || changes.longitudePointer?.firstChange) {
          this.originalLatitude = this.latitudePointer;
          this.originalLongitude = this.longitudePointer;
        }
        if (this.isRadiusSelectionEnable) {
          const center = fromLonLat([this.longitudePointer, this.latitudePointer]);
          this.addRadiusCircle([center[0], center[1]], this.radius);
        }
      }
    }
  }

  addMarker(coordinates: [number, number], isAnimated: boolean = false) {
    const transformedCoordinates: any = fromLonLat(coordinates);
    let markerFeature: any = new Feature({
      geometry: new Point(transformedCoordinates),
    });
    let vectorSource: any = new VectorSource({
      features: [markerFeature],
    });
    // استفاده از آیکون موجود location.svg برای تست
    const pinIcon = '/icons/map-pin-animated.svg';
    const markerStyle: any = new Style({
      image: new Icon({
        src: pinIcon,
        anchor: [0.5, 1],
        scale: 1.2,
        rotation: 0
      }),
    });
    markerFeature.setStyle(markerStyle);
    if (this.markerLayer) {
      this.map?.removeLayer(this.markerLayer);
    }
    this.markerLayer = new VectorLayer({
      source: vectorSource,
    });
    this.map?.addLayer(this.markerLayer);
    this.latitudePointerChange.emit(this.latitudePointer);
    this.longitudePointerChange.emit(this.longitudePointer);
    if (this.isAddressDetailEnable) {
      this.getPointerInformation();
    }
  }

  addRadiusCircle(center: [number, number], radius: number): void {
    const circleFeature = new Feature({
      geometry: new Circle(center, radius),
    });
    const circleStyle = new Style({
      fill: new Fill({
        color: 'rgba(255, 0, 0, 0.2)'
      }),
      stroke: new Stroke({
        color: 'red',
        width: 2
      }),
      image: new CircleStyle({
        radius: 6,
        fill: new Fill({
          color: 'red'
        })
      })
    });
    circleFeature.setStyle(circleStyle);
    const vectorSource = new VectorSource({
      features: [circleFeature],
    });
    if (this.radiusLayer) {
      this.map?.removeLayer(this.radiusLayer);
    }
    this.radiusLayer = new VectorLayer({
      source: vectorSource,
    });
    this.map?.addLayer(this.radiusLayer);
  }

  updateRadius(newRadius: number): void {
    this.radius = newRadius;
    if (this.map && this.markerLayer) {
      const markerFeatures = this.markerLayer.getSource()?.getFeatures();
      if (markerFeatures && markerFeatures.length > 0) {
        const markerGeometry = markerFeatures[0].getGeometry();
        if (markerGeometry instanceof Point) {
          const center: any = markerGeometry.getCoordinates();
          this.addRadiusCircle(center, this.radius);
        }
      }
    }
  }

  getPointerInformation(): void {
    const service = this.geoReverseService.replace('{latitude}', `${this.latitudePointer}`).replace('{longitude}', `${this.longitudePointer}`);
    this.httpClient.get(service).subscribe((data: any) => {
      if (data) {
        this.addressDetail.emit(this.extractAddressDetails(data));
      }
    });
  }

  extractAddressDetails(addressDetail: any): any {
    const result: any = {};
    result.PlaceOd = addressDetail.place_id || 'N/A';
    result.Licence = addressDetail.licence || 'N/A';
    result.OsmType = addressDetail.osm_type || 'N/A';
    result.OsmId = addressDetail.osm_id || 'N/A';
    result.Latitude = addressDetail.lat || 'N/A';
    result.Longitude = addressDetail.lon || 'N/A';
    result.Category = addressDetail.category || 'N/A';
    result.Type = addressDetail.type || 'N/A';
    result.PlaceRank = addressDetail.place_rank || 'N/A';
    result.Importance = addressDetail.importance || 'N/A';
    result.Addresstype = addressDetail.addresstype || 'N/A';
    result.Name = addressDetail.name || 'N/A';
    result.DisplayName = addressDetail.display_name || 'N/A';
    result.Address = addressDetail.address ? {
      Road: addressDetail.address.road || 'N/A',
      Neighbourhood: addressDetail.address.neighbourhood || 'N/A',
      Suburb: addressDetail.address.suburb || 'N/A',
      City: addressDetail.address.city || 'N/A',
      District: addressDetail.address.district || 'N/A',
      County: addressDetail.address.county || 'N/A',
      Province: addressDetail.address.province || 'N/A',
      ISO3166: addressDetail.address.ISO3166 || 'N/A',
      Postcode: addressDetail.address.postcode ? addressDetail.address.postcode.replaceAll('-', '') : 'N/A',
      Country: addressDetail.address.country || 'N/A',
      CountryCode: addressDetail.address.country_code || 'N/A'
    } : {};
    result.BoundingBox = addressDetail.boundingbox || [];
    return result;
  }

  /**
   * باز کردن دیالوگ انتخاب اپلیکیشن نقشه در دستگاه
   * این متد دیالوگ پیش‌فرض سیستم را باز می‌کند که تمام اپلیکیشن‌های نقشه نصب‌شده را نشان می‌دهد
   */
  private openMapInDevice(latitude: number, longitude: number): void {
    if (!isPlatformBrowser(Config.platformId)) {
      return;
    }
    const title = this.addressTitle || 'موقعیت';
    const userAgent = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);
    if (isAndroid) {
      // برای Android: استفاده از geo: که دیالوگ انتخاب اپلیکیشن را باز می‌کند
      // این دیالوگ تمام اپلیکیشن‌های نقشه نصب‌شده (Google Maps, Waze, Maps.me, OsmAnd, etc.) را نشان می‌دهد
      const address = encodeURIComponent(title);
      const geoUrl = `geo:${latitude},${longitude}?q=${latitude},${longitude}(${address})`;
      // استفاده از geo: که دیالوگ انتخاب اپلیکیشن Android را باز می‌کند
      window.location.href = geoUrl;
    } else if (isIOS) {
      // برای iOS: استفاده از maps:// که دیالوگ انتخاب اپلیکیشن را باز می‌کند
      // اگر چند اپلیکیشن نقشه نصب باشد (مثل Google Maps, Waze, etc.)، دیالوگ انتخاب نمایش داده می‌شود
      const address = encodeURIComponent(title);
      const mapsUrl = `maps://maps.apple.com/?q=${address}&ll=${latitude},${longitude}`;
      // تلاش برای باز کردن Apple Maps یا دیالوگ انتخاب
      window.location.href = mapsUrl;
    } else {
      // برای دسکتاپ: مستقیماً Google Maps را باز می‌کنیم
      const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(url, '_blank');
    }
  }
}
